import { useCallback, useEffect, useRef, useState } from 'react';
import { requestJSON } from '../lib/request';

export const previewSnapshotKey = design => design ? JSON.stringify([design.model_id, design.layout, !!design.sd_pocket, design.window_shape || 'rectangle', design.height_in, design.run_a, design.run_b, design.finish, design.dividers, design.openings, design.screens, design.photo_overlay ? { ...design.photo_overlay, opening_regions: design.photo_overlay.opening_regions || [] } : null]) : '';
export function useAIPreview({ designId, photoId, save, apiRef }) {
  const [jobs, setJobs] = useState([]), [selectedId, setSelectedId] = useState(null);
  const [preparing, setPreparing] = useState(false), [error, setError] = useState(''), [show, setShow] = useState(false);
  const submitting = useRef(false), requestedJob = useRef(null), mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { setJobs([]); setSelectedId(null); setShow(false); setError(''); }, [photoId]);
  useEffect(() => {
    if (!designId || !photoId) return;
    const controller = new AbortController(); let timer;
    const poll = async () => {
      try {
        const rows = await requestJSON(`/configurations/${designId}/ai-previews?photo_id=${photoId}`, { signal: controller.signal });
        if (controller.signal.aborted) return;
        setJobs(rows); setError(previous => previous === 'AI preview status could not be loaded. Your exact preview is unchanged.' ? '' : previous);
        if (!requestedJob.current) requestedJob.current = rows.find(job => ['queued', 'generating'].includes(job.status))?.id || null;
        const requested = rows.find(job => job.id === requestedJob.current);
        if (requested?.status === 'succeeded') { setSelectedId(requested.id); setShow(true); requestedJob.current = null; }
        else if (requested?.status === 'failed') requestedJob.current = null;
        setSelectedId(previous => rows.some(row => row.id === previous) ? previous : rows.find(row => row.status === 'succeeded' || row.cleaned_available)?.id || null);
        if (rows.some(job => ['queued', 'generating'].includes(job.status)) || submitting.current) timer = setTimeout(poll, 2500);
      } catch (err) {
        if (!controller.signal.aborted) { setError('AI preview status could not be loaded. Your exact preview is unchanged.'); timer = setTimeout(poll, 7000); }
      }
    };
    poll(); return () => { controller.abort(); clearTimeout(timer); };
  }, [designId, photoId, preparing]);
  const generate = useCallback(async () => {
    if (submitting.current || !photoId) return;
    submitting.current = true; setPreparing(true); setError(''); setShow(false);
    try {
      const reference = await apiRef.current.capturePhoto({ reference: true });
      const layer = await apiRef.current.capturePhoto({ reference: true, layer: true });
      const id = await save();
      const body = new FormData(); body.append('request_id', crypto.randomUUID()); body.append('reference', reference, 'placed-door.png');
      body.append('door_layer', layer, 'isolated-door.png');
      const job = await requestJSON(`/configurations/${id}/ai-previews`, { method: 'POST', body, timeoutMs: 120000 });
      if (!mounted.current) return;
      setJobs(previous => [job, ...previous.filter(row => row.id !== job.id)]); requestedJob.current = job.id;
      if (job.status === 'succeeded') { setSelectedId(job.id); setShow(true); requestedJob.current = null; }
    } catch (err) { if (mounted.current) setError(err.message); }
    finally { submitting.current = false; if (mounted.current) setPreparing(false); }
  }, [apiRef, photoId, save]);
  const retryInstall = async jobId => {
    if (submitting.current) return;
    submitting.current = true; setPreparing(true); setError('');
    try {
      const job = await requestJSON(`/ai-previews/${jobId}/retry-install`, { method: 'POST' });
      requestedJob.current = job.id; setJobs(previous => [job, ...previous.filter(row => row.id !== job.id)]);
    } catch (err) { setError(err.message); }
    finally { submitting.current = false; setPreparing(false); }
  };
  const selected = jobs.find(job => job.id === selectedId && (job.status === 'succeeded' || job.cleaned_available));
  const active = jobs.find(job => ['queued', 'generating'].includes(job.status));
  return { jobs, selected, show: show && !!selected, setShow, error, preparing, active, busy: preparing || !!active, generate, retryInstall, select: id => { setSelectedId(id); setShow(true); } };
}